import React, { useState } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogContentText,
  CircularProgress, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper
} from '@mui/material';
import sendRequest from '../utils/sendRequest';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { MenuButtonIcon, SubmitButton, CancelButton } from "../styled/CustomButtons";
import { CustomDialogTitle } from "../styled/DialogTitles";

export default function MarkReceivedDialog({ po_doc_id, poName, addSnackbar, fetchPoDetails=null, fetchPos=null, isIcon=false }) {
  const [open, setOpen] = useState(false);
  const [loadingReceived, setLoadingReceived] = useState(false);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);
  const [discrepancyReport, setDiscrepancyReport] = useState([]);

  const fetchDiscrepancyReport = async () => {
    setLoadingDiscrepancies(true);
    try {
      let response = await sendRequest(`purchase-orders/discrepancy-report?po_doc_id=${po_doc_id}`);
      if (response.has_discrepancy) {
        setHasDiscrepancy(true);
        setDiscrepancyReport(response.report);
      } else {
        setHasDiscrepancy(false);
        setDiscrepancyReport([]);
      }
    } catch (error) {
      addSnackbar(error.message, false);
      handleClose();
    } finally {
      setLoadingDiscrepancies(false);
    }
  }

  const handleOpen = async () => {
    setHasDiscrepancy(false);
    setDiscrepancyReport([]);
    setOpen(true);
    await fetchDiscrepancyReport();
  };

  const handleClose = () => {
    if (!loadingReceived) {
      setOpen(false);
    }
  };

  const markReceived = async () => {
    setLoadingReceived(true);
    try {
      let response = await sendRequest(`purchase-orders/mark-received?po_doc_id=${po_doc_id}`);
      addSnackbar(response.message);
      handleClose();
      isIcon ? fetchPos() : fetchPoDetails();
    } catch (error) {
      addSnackbar(error.message, false);
    } finally {
      setLoadingReceived(false);
    }
  }

  return (
    <Box>
      <MenuButtonIcon
        isIcon={isIcon}
        title={"Mark Received"}
        text={"Mark Receiving Completed"}
        icon={AssignmentTurnedInIcon}
        onClick={handleOpen}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={loadingReceived}
        fullWidth
        maxWidth="md"
      >
        <CustomDialogTitle
          header={"Mark as Receiving Completed"}
          poName={poName}
        />
        <DialogContent>
          {loadingDiscrepancies ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={36} />
              <Typography mt={2}>Checking for discrepancies...</Typography>
            </Box>
          ) : (
            <>
              <DialogContentText>
                Are you finished receiving the order?
              </DialogContentText>

              {hasDiscrepancy ? (
                <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        {["SKU", "Qty Submitted", "Qty Received", "Unit Discrepancy", "Value Discrepancy"].map((header) => (
                          <TableCell
                            key={header}
                            align={header === "SKU" ? "left" : "right"}
                            sx={{
                              position: 'sticky',
                              top: 0,
                              backgroundColor: '#555555',
                              color: 'white',
                              fontWeight: 'bold',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              zIndex: 1
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {discrepancyReport.map((row, index) => {
                        const submitted = row["Qty Submitted"];
                        const received = row["Qty Received"];
                        const isOver = received > submitted;
                        const isUnder = received < submitted;
                        const isUnsubmitted = submitted === 0;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: isUnsubmitted ? '#fff9e6' : isOver ? '#e6f4ea' : isUnder ? '#fdecea' : 'inherit',
                            }}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row["SKU"]}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{submitted}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{received}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row["Unit Discrepancy"]}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row["Value Discrepancy"]}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography mt={2}>No discrepancies found.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={handleClose} disabled={loadingReceived}>Cancel</CancelButton>
          <SubmitButton
            showSpinner={loadingReceived}
            text={"Mark Received"}
            onClick={() => markReceived()}
            disabled={loadingReceived || loadingDiscrepancies}
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}